import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface MarkScopeManagedItem {
  id: number;
  code: string;
  name: string;
  supplierName: string;
  quantity: number;
}

interface MarkScopeManagedDialogProps {
  open: boolean;
  message: string;
  items: MarkScopeManagedItem[];
  busy?: boolean;
  onConfirm: (productIds: number[]) => void | Promise<void>;
  onClose: () => void;
}

function formatQty(value: number): string {
  if (!Number.isFinite(value)) return '0';
  return Number.isInteger(value) ? String(value) : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

/**
 * Confirmación de SUP-LOT-009: dar por gestionadas las filas on_demand de L.
 * Todas marcadas al abrir; las desmarcadas se quedan en la tabla.
 */
export function MarkScopeManagedDialog({
  open,
  message,
  items,
  busy = false,
  onConfirm,
  onClose,
}: MarkScopeManagedDialogProps) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (open) {
      setSelected(new Set(items.map((item) => item.id)));
      setQuery('');
    }
    // Solo al abrir: recargas de la tabla con el modal abierto no deshacen lo desmarcado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const visibleItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (item) => item.code.toLowerCase().includes(q) || item.name.toLowerCase().includes(q),
    );
  }, [items, query]);

  if (!open) return null;

  const titleId = 'mark-scope-managed-dialog-title';
  const selectedIds = items.filter((item) => selected.has(item.id)).map((item) => item.id);
  const allVisibleSelected = visibleItems.length > 0 && visibleItems.every((item) => selected.has(item.id));

  const toggle = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      visibleItems.forEach((item) => {
        if (allVisibleSelected) next.delete(item.id);
        else next.add(item.id);
      });
      return next;
    });
  };

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-[1px]"
        onClick={busy ? undefined : onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl border border-gray-200 bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h2 id={titleId} className="text-base font-semibold text-gray-900">
              {t('supply.productionScope.markManaged.title')}
            </h2>
            <p className="mt-1 text-sm text-gray-600">{message}</p>
            <p className="mt-2 text-xs text-gray-500">
              {t('supply.productionScope.markManaged.uncheckHint')}{' '}
              {t('supply.productionScope.markManaged.reopenHint')}
            </p>
          </div>
          {!busy ? (
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label={t('supply.productionScope.markManaged.cancel')}
            >
              <X className="h-5 w-5" />
            </button>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-5 pt-4">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={allVisibleSelected}
              onChange={toggleVisible}
              disabled={busy || visibleItems.length === 0}
              className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            {t('supply.productionScope.markManaged.selectAll')}
          </label>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('supply.productionScope.markManaged.search')}
            disabled={busy}
            className="w-56 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          />
        </div>

        <div className="mx-5 mt-3 min-h-0 flex-1 overflow-y-auto rounded-md border border-gray-200">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-50 text-left text-xs font-medium text-gray-600">
              <tr>
                <th className="w-10 px-3 py-2" />
                <th className="px-3 py-2">{t('supply.table.code')}</th>
                <th className="px-3 py-2">{t('supply.table.product')}</th>
                <th className="px-3 py-2">{t('supply.table.supplier')}</th>
                <th className="px-3 py-2 text-right">{t('supply.table.suggested')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visibleItems.map((item) => (
                <tr
                  key={item.id}
                  className={`cursor-pointer hover:bg-gray-50 ${selected.has(item.id) ? '' : 'text-gray-400'}`}
                  onClick={() => !busy && toggle(item.id)}
                >
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(item.id)}
                      onChange={() => toggle(item.id)}
                      onClick={(e) => e.stopPropagation()}
                      disabled={busy}
                      className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    />
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 font-medium">{item.code}</td>
                  <td className="px-3 py-2">{item.name}</td>
                  <td className="px-3 py-2">{item.supplierName || '—'}</td>
                  <td className="px-3 py-2 text-right">{formatQty(item.quantity)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {visibleItems.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-gray-500">
              {t('supply.productionScope.markManaged.noMatches')}
            </p>
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-4">
          <span className="text-xs text-gray-500">
            {t('supply.productionScope.markManaged.selected', { selected: selectedIds.length, total: items.length })}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
              disabled={busy}
              onClick={onClose}
            >
              {t('supply.productionScope.markManaged.cancel')}
            </button>
            <button
              type="button"
              className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
              disabled={busy || selectedIds.length === 0}
              onClick={() => void onConfirm(selectedIds)}
            >
              {busy
                ? t('supply.productionScope.markManaged.marking')
                : t('supply.productionScope.markManaged.confirm', { count: selectedIds.length })}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
